"use client";

import { COMPANY, CONTACT_PHONE, SUPPORT_EMAIL, SUPPORT_HOURS, isFilled } from "../../../_lib/site";
import { useSite } from "../../../_lib/useSite";
import { useSimpleLegal } from "./LegalSheet";

/*
 * 법정 표시 푸터 — 시안 §10 (`docs/design/simple-skin/README.md`).
 *
 * 🔴 **전자상거래법 제10조·제12조 — 사이버몰 초기화면 표시 의무**입니다. 이 스킨은 초기화면이 곧
 *    구매 화면이라 **셸에 두어 전 화면 하단에** 붙입니다(README §10 «푸터는 셸에 둡니다»).
 * 🔴 **값은 `site.ts` 에서만 읽습니다**(`COMPANY` · `CONTACT_PHONE` · `SUPPORT_HOURS` · `SUPPORT_EMAIL`).
 *    채워지지 않은 항목(빈 값 · `CHANGE_ME`)은 **그리지 않습니다**(`isFilled`) — 자리표시가 공개
 *    화면에 나가는 것보다 빈 편이 낫습니다(테스트 공유 단계 · `site.ts` 끝의 가드 주석).
 * 🔑 약관은 **본문이 채워진 것만**(`useSite().legal`) — 누르면 시트가 열립니다(주소를 갈지 않음).
 *    개인정보 처리방침은 관례(개인정보 보호법 제30조 «눈에 띄게»)대로 굵게.
 * 🔑 «사업자정보 확인» 은 공정거래위원회 사업자정보 공개 페이지입니다 — 사업자등록번호가 있을 때만.
 */
const ESCROW_URL: string = COMPANY.escrowUrl;
const ESCROW_PROVIDER: string = COMPANY.escrowProvider;

function bizCheckUrl(bizNo: string): string | null {
  const digits = bizNo.replace(/\D/g, "");
  return digits.length === 10 ? `https://www.ftc.go.kr/bizCommPop.do?wrkr_no=${digits}` : null;
}

export function Footer() {
  const { brand, legal } = useSite();
  const sheet = useSimpleLegal();

  const phone = isFilled(CONTACT_PHONE) ? CONTACT_PHONE : "";
  const company = isFilled(COMPANY.name) ? COMPANY.name : "";
  const ceo = isFilled(COMPANY.ceo) ? COMPANY.ceo : "";
  const bizNo = isFilled(COMPANY.bizNo) ? COMPANY.bizNo : "";
  const mailOrder = isFilled(COMPANY.mailOrderNo) ? COMPANY.mailOrderNo : "";
  const address = isFilled(COMPANY.address) ? COMPANY.address : "";
  const hosting = isFilled(COMPANY.hostingProvider) ? COMPANY.hostingProvider : "";
  const email = SUPPORT_EMAIL;
  const bizUrl = bizNo ? bizCheckUrl(bizNo) : null;
  const holder = company || brand.siteName;
  const hasEscrow = isFilled(ESCROW_PROVIDER) || isFilled(ESCROW_URL);

  /* 한 줄씩 — 빈 항목은 줄째 빠집니다. 모바일은 줄마다, PC 는 몇 줄을 «·» 로 잇습니다(시안 01 · 14). */
  const line1 = [company && `상호 ${company}`, ceo && `대표 ${ceo}`].filter(Boolean).join(" · ");
  const bizLine = bizNo ? (
    <>
      사업자등록번호 <span className="tabular-nums">{bizNo}</span>
      {bizUrl && (
        <a
          href={bizUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="공정거래위원회 사업자정보 공개 페이지를 새 창으로 엽니다."
          className="ml-[3px] inline-block rounded-[var(--tnf-radius-min)] bg-surface px-[7px] pt-px pb-0.5 text-[length:calc(var(--tnf-text-base)*11.5/14)] leading-normal text-ink-soft shadow-[inset_0_0_0_1px_var(--tnf-line)] hover:text-ink"
        >
          사업자정보 확인<span className="sr-only"> (새 창)</span>
        </a>
      )}
    </>
  ) : null;
  const mailLine = mailOrder ? `통신판매업신고 ${mailOrder}` : "";
  const contactLine = [email, hosting && `호스팅 ${hosting}`, isFilled(ESCROW_PROVIDER) && `구매안전서비스 ${ESCROW_PROVIDER}`].filter(Boolean).join(" · ");

  const links = (
    <>
      {legal.map((doc) => (
        <button
          key={doc.kind}
          type="button"
          onClick={() => sheet.open(doc.kind)}
          title={`${doc.title} 전문을 이 화면 위에 엽니다.`}
          className={`hover:text-ink ${doc.kind === "privacy" ? "font-bold text-ink" : "font-medium text-ink-soft"}`}
        >
          {doc.title}
        </button>
      ))}
      {hasEscrow && isFilled(ESCROW_URL) && (
        <a href={ESCROW_URL} target="_blank" rel="noopener noreferrer" title="구매안전서비스 가입 사실을 새 창에서 확인합니다." className="font-medium text-ink-soft hover:text-ink">
          구매안전서비스 확인<span className="sr-only"> (새 창)</span>
        </a>
      )}
    </>
  );
  const hasLinks = legal.length > 0 || (hasEscrow && isFilled(ESCROW_URL));

  return (
    <footer className="flex-none border-t border-line bg-ground">
      {/* ── 모바일 (시안 01) ── */}
      <div className="px-4 pt-5 pb-[calc(26px+env(safe-area-inset-bottom))] lg:hidden">
        {phone && (
          <div className="flex items-baseline gap-[9px]">
            <span className="text-[length:calc(var(--tnf-text-base)*12/14)] font-bold tracking-[0.06em] text-ink-faint">고객센터</span>
            <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="text-[length:calc(var(--tnf-text-base)*19/14)] font-bold tracking-[-0.02em] text-ink tabular-nums">{phone}</a>
            <span className="ml-auto text-[length:calc(var(--tnf-text-base)*12/14)] text-ink-faint">{SUPPORT_HOURS.weekday}</span>
          </div>
        )}
        {hasLinks && <nav aria-label="약관" className={`flex flex-wrap gap-x-3.5 gap-y-[7px] text-[length:calc(var(--tnf-text-base)*12.5/14)] ${phone ? "mt-[15px]" : ""}`}>{links}</nav>}
        <p className="mt-[13px] text-[length:calc(var(--tnf-text-base)*12/14)] leading-[1.95] text-ink-faint">
          {[line1, bizLine, mailLine, address, contactLine].filter(Boolean).map((line, index) => (
            <span key={index} className="block">{line}</span>
          ))}
        </p>
        <p className="mt-3 text-[length:calc(var(--tnf-text-base)*11.5/14)] text-ink-faint/80">© {new Date().getFullYear()} {holder}</p>
      </div>

      {/* ── PC (시안 14 · 15) ── */}
      <div className="mx-auto hidden w-full max-w-[1120px] grid-cols-[320px_minmax(0,1fr)] gap-[60px] px-10 pt-[46px] pb-[50px] lg:grid xl:px-0">
        <div>
          {phone && (
            <>
              <div className="text-[length:calc(var(--tnf-text-base)*12.5/14)] font-bold tracking-[0.08em] text-ink-faint">고객센터</div>
              <div className="mt-2 text-[length:calc(var(--tnf-text-base)*26/14)] font-bold tracking-[-0.02em] text-ink tabular-nums">{phone}</div>
              <div className="mt-2 text-[length:calc(var(--tnf-text-base)*13.5/14)] leading-[1.7] text-ink-soft">
                {[SUPPORT_HOURS.weekday, SUPPORT_HOURS.lunchBreak].filter(Boolean).join(" · ")}
                {SUPPORT_HOURS.closed && <><br />{SUPPORT_HOURS.closed}</>}
              </div>
            </>
          )}
        </div>
        <div>
          {hasLinks && <nav aria-label="약관" className="flex flex-wrap gap-x-[18px] gap-y-2 text-[length:calc(var(--tnf-text-base)*13.5/14)]">{links}</nav>}
          <div className={`text-[length:calc(var(--tnf-text-base)*12.5/14)] leading-[2.05] text-ink-faint ${hasLinks ? "mt-4" : ""}`}>
            {[line1 || bizLine ? <>{line1}{line1 && bizLine ? " · " : ""}{bizLine}</> : null, [mailLine, address].filter(Boolean).join(" · "), contactLine]
              .filter(Boolean)
              .map((line, index) => (
                <span key={index} className="block">{line}</span>
              ))}
          </div>
          <div className="mt-3.5 text-[length:calc(var(--tnf-text-base)*12.5/14)] text-ink-faint">© {new Date().getFullYear()} {holder}</div>
        </div>
      </div>
      {sheet.sheet}
    </footer>
  );
}
